// Include navbar-type-1 layout styles for the SSR version.
// SSR optimization: This component renders only on client (Build.isBrowser).
// The placeholder (sn-navbar-placeholder-type-1) handles SSR with SEO links.
@import '../../../../navbars/type-1/index';

// =============================================================================
// SSR Transition Strategy: Instant Switch
// =============================================================================
// 1. Placeholder renders during SSR, occupies layout space
// 2. Full navbar renders hidden on client
// 3. Once navbar is hydrated, instantly show navbar and hide placeholder
// No animations - instant switch to prevent any visual artifacts
// =============================================================================

// Placeholder base styles
sn-navbar-placeholder-type-1 {
  display: block;
  position: relative;
  z-index: 103;
}

// Hide placeholder when full navbar is ready
sn-navbar-placeholder-type-1[hidden] {
  display: none !important;
}

// =============================================================================
// Mobile breakpoint override for SSR and pre-hydration (960px)
// =============================================================================
// The navbar-type-1 component uses mobileBreakpoint={960} but the CSS
// media queries only handle 768px (below-desktop). The JS-based
// is-mobile-view class only works after hydration.
// For SSR placeholder AND main navbar before hydration, we need CSS-only
// solution to match 960px breakpoint and prevent visual mismatch.
// =============================================================================
@media only screen and (width <= 960px) {
  // Placeholder navbar
  sn-navbar-placeholder-type-1 .navbar-type-1--logo-nav-first {
    .navbar-type-1__nav-column {
      display: none;
    }

    .navbar-type-1__actions-column {
      display: none;

      // Keep search icon visible on mobile
      me-button[icon="interface--search"] {
        display: inline-flex !important;
      }
    }

    .navbar-type-1__mobile-toggle {
      display: flex;
      order: 3;
    }
  }

  // Main navbar (before hydration adds is-mobile-view class)
  sn-navbar-type-1 .navbar-type-1--logo-nav-first {
    .navbar-type-1__nav-column {
      display: none;
    }

    .navbar-type-1__actions-column {
      display: none;

      // Keep search icon visible on mobile
      me-button[icon="interface--search"] {
        display: inline-flex !important;
      }
    }

    .navbar-type-1__mobile-toggle {
      display: flex;
      order: 3;
    }
  }
}

// SSR deferred state - completely hidden, no space
sn-navbar-type-1.sn-navbar-type-1--ssr-deferred {
  display: none !important;
}

// Loading state - navbar hidden and not in document flow
// CRITICAL: Hide all dropdown menus to prevent FOUC before styles are applied
sn-navbar-type-1.sn-navbar-type-1--loading {
  display: none !important;
}

// Ready state - navbar visible and sticky.
// Sticky positioning is on the OUTER wrapper (sn-navbar-type-1), NOT on the inner navbar-type-1.
// The inner navbar-type-1 has sticky="always" for scroll/box-shadow logic,
// but its .is-sticky-host class is overridden here to prevent double sticky.
sn-navbar-type-1.sn-navbar-type-1--ready {
  display: block;
  position: sticky;
  top: 0;
  visibility: visible;
  width: auto;
  height: auto;
  overflow: visible;
  clip: auto;
  pointer-events: auto;
  z-index: 105;

  // Override inner navbar sticky: the outer wrapper handles sticky positioning.
  // Without this, both elements would be position: sticky (double sticky).
  navbar-type-1.is-sticky-host {
    position: static;
    z-index: auto;
  }

  // When search is opened, increase z-index to appear above google-button (z-index: 104)
  &.sn-navbar-type-1--search-opened {
    z-index: 106;
  }

}

// Keep navbar above social auth button when any dropdown is opened.
sn-navbar-type-1.sn-navbar-type-1--ready:has(.me-dropdown__menu--open) {
  z-index: 106;
}

.theme-brand-signnow {
  .navbar-type-1 {
    border-bottom: none;
    z-index: 103;

    // Inline SVG logo styling (replaces me-logo component)
    a.me-logo {
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      min-width: 92px;

      svg {
        display: block;
        height: auto;
        width: auto;
        max-height: 42px;
      }
    }

    .me-logo {
      min-width: 92px;
    }

    &--logo-nav-first {
      .navbar-type-1__logo-column {
        margin-right: $spacing-xl;
      }

      .navbar-type-1__nav-column {
        gap: $spacing-md;

        @include below($breakpoint-desktop-md + 58px) {
          gap: $spacing-2xs;
        }
      }
    }
  }

  .dropdown-menu-type-1 {
    gap: 0;
  }

  .dropdown-menu-type-2__navigation {
    flex: 0 0 72%;
    max-width: 72%;
    padding: map-get($map: $spacings, $key: xl) 0;

    @include desktop {
      padding: map-get($map: $spacings, $key: 2xl) 0;
    }
  }

  .dropdown-menu-type-2__featured {
    flex: 0 0 28%;
    max-width: 28%;
    padding: map-get($map: $spacings, $key: 2xl) 0 map-get($map: $spacings, $key: 2xl) map-get($map: $spacings, $key: 2xl);

    .dropdown-menu-type-2__element-item .featured-card__title {
      font-size: map-get($font-sizes, md);
      font-weight: map-get($font-weights, bold);
      color: var(--color-neutral-black);
    }
  }

  .dropdown-menu-type-2__element-item:not(:last-child) {
    margin-bottom: map-get($map: $spacings, $key: lg);
  }

  .dropdown-menu-type-2__wrapper {
    border-bottom: none;
  }

  hero-header-type-1 {
    .picture {
      background-color: transparent;
    }
  }

  .dropdown-link-icon {
    path {
      fill: transparent;
    }
  }

  // Blue accent buttons (primary CTA links in dropdowns)
  .me-button.me-button--blue {
    color: $background-color-brand-primary;
    font-weight: map-get($font-weights, bold);

    .me-button__title {
      font-weight: map-get($font-weights, bold);
    }

    &::before {
      background-color: $background-color-brand-primary;
    }

    // Keep blue color on hover for both text and icon
    &:hover,
    &:focus {
      color: $background-color-brand-primary;

      .me-button__icon {
        color: $background-color-brand-primary;
      }
    }
  }

  // Bold font for specific buttons
  .me-button--bold .me-button__title {
    font-weight: map-get($font-weights, bold);
  }

  .dropdown-menu-type-2__featured-card {
    .featured-card__title {
      font-size: map-get($font-sizes, md);
      font-weight: map-get($font-weights, bold);
      color: var(--color-neutral-black);
    }

    .featured-card__description {
      font-size: map-get($font-sizes, sm);
      line-height: map-get($line-heights, lg);
      color: var(--color-neutral-black);
    }
  }

  .dropdown-menu-type-2__features {
    margin-bottom: 0;

    .featured-card__content {
      justify-content: space-between;
      margin-top: map-get($map: $spacings, $key: 2xl);

      .featured-card__text {
        flex: none;

        .me-button {
          padding: map-get($map: $spacings, $key: xs);
          border-color: $color-neutral-neutral;
          border-width: map-get($map: $border-widths, $key: sm);
          border-radius: 50%;
        }
      }
    }
  }

  .dropdown-menu-type-2__top-integrations {
    .featured-card__content {
      margin-top: map-get($map: $spacings, $key: lg);
    }

    .dropdown-menu-type-2__featured-list {
      display: flex;
      flex-direction: column;
      gap: map-get($map: $spacings, $key: lg);
    }
  }

  .dropdown-menu-type-2__documentation {
    margin-bottom: 0;

    .featured-card__content {
      justify-content: space-between;
      align-items: flex-end;
      margin-top: map-get($map: $spacings, $key: lg);

      .featured-card__picture svg {
        margin-left: -#{map-get($map: $spacings, $key: lg)};
        margin-bottom: -#{map-get($map: $spacings, $key: lg)};
      }

      .featured-card__text {
        flex: none;

        .me-button {
          padding: map-get($map: $spacings, $key: xs);
          border-color: $color-neutral-neutral;
          border-width: map-get($map: $border-widths, $key: sm);
          border-radius: 50%;
        }
      }
    }
  }

  // Search icon in navbar
  .search-icon {
    @include size(32px);
  }
}
